import React from 'react';
import { ContentCardPlatformIcon } from '~/components/ContentCard/components/ContentCardPlatformIcon';
import { IconBox } from '~/components/IconBox';
import {
  ALL_SOCIAL_PLATFORM_TYPE,
  MONTH_NAME_DAY_YEAR_FORMAT,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useGetContent } from '~/hooks/api/useGetContent';
import {
  IApiSearchContent,
  IApiSearchYoutubeContent,
} from '~/searchApi/content';
import { TTAContent } from '~/types/tta';
import { formatDate } from '~/utils';

import { formatPlatformName } from '../utils/formatters';

// Global cache to track loaded images across component mounts
const imageLoadedCache = new Map<string, boolean>();

interface ContentItemProps {
  content: TTAContent;
  isSelected?: boolean;
  onClick?: () => void;
  showInfo?: boolean;
}

export const ContentItem = React.forwardRef<HTMLDivElement, ContentItemProps>(
  ({ content, isSelected, onClick, showInfo = false }, ref) => {
    const { data } = useGetContent(content.content_id);
    const contentData = data?.data?.[0];

    const generatePreviewUrl = (contentData: IApiSearchContent): string => {
      if (contentData.platform === SOCIAL_PLATFORMS.youtube) {
        const youtubeContent = contentData as IApiSearchYoutubeContent;
        return (
          youtubeContent.thumbnails?.standard?.url ||
          contentData.assets[3]?.url ||
          ''
        );
      }
      return contentData.assets[0]?.previewUrl || '';
    };

    const imageUrl = contentData ? generatePreviewUrl(contentData) : '';
    const [imageLoaded, setImageLoaded] = React.useState(() =>
      imageUrl ? imageLoadedCache.get(imageUrl) || false : false,
    );
    const [imageError, setImageError] = React.useState(false);

    // Ref callback to check cache status immediately when image is rendered
    const imageRefCallback = React.useCallback(
      (img: HTMLImageElement | null) => {
        if (!img || !imageUrl) return;

        // First check global cache - if we've loaded this URL before, use it immediately
        if (imageLoadedCache.get(imageUrl)) {
          setImageLoaded(true);
          setImageError(false);
          return;
        }

        // Check if image is already cached in browser
        const checkCacheStatus = () => {
          if (img.complete && img.naturalWidth > 0) {
            setImageLoaded(true);
            setImageError(false);
            imageLoadedCache.set(imageUrl, true); // Store in global cache
          } else if (img.complete && img.naturalWidth === 0) {
            setImageError(true);
            setImageLoaded(false);
          } else {
            setImageLoaded(false);
            setImageError(false);
          }
        };

        checkCacheStatus();
        setTimeout(checkCacheStatus, 0);
      },
      [imageUrl],
    );

    const handleImageLoad = () => {
      setImageLoaded(true);
      setImageError(false);

      // Store in global cache for future mounts
      if (imageUrl) {
        imageLoadedCache.set(imageUrl, true);
      }

      // Track successful image load
      trackEvent('foam-tta-content-item-image-load', {
        contentId: content.content_id,
        platform: contentData?.platform || 'unknown',
        showInfo,
        isSelected: isSelected || false,
      });
    };

    const handleImageError = () => {
      setImageLoaded(false);
      setImageError(true);

      // Track image load errors
      trackEvent('foam-tta-content-item-image-error', {
        contentId: content.content_id,
        platform: contentData?.platform || 'unknown',
        showInfo,
        isSelected: isSelected || false,
        previewUrl: contentData ? generatePreviewUrl(contentData) : 'unknown',
      });
    };

    const handleContentClick = () => {
      // Track content item clicks
      trackEvent('foam-tta-content-item-click', {
        contentId: content.content_id,
        platform: contentData?.platform || 'unknown',
        showInfo,
        isSelected: isSelected || false,
        hasRelevantSnippet: !!content.relevant_snippet,
      });

      if (onClick) {
        onClick();
      }
    };

    if (showInfo) {
      return (
        <div
          className="flex w-full min-w-0 flex-row"
          onClick={handleContentClick}
        >
          {/* Thumbnail */}
          <div className="mr-4 shrink-0 overflow-hidden rounded-8 bg-neutral-100">
            {imageError || (contentData && !imageUrl) ? (
              <div className="flex size-12 items-center justify-center rounded-8 bg-neutral-100">
                <div className="text-12 text-neutral-300">N/A</div>
              </div>
            ) : contentData ? (
              <div className="relative">
                {/* Skeleton loader */}
                {!imageLoaded && (
                  <div className="size-12 rounded-8 bg-feedback-skeleton-background" />
                )}
                {/* Actual image */}
                <img
                  ref={imageRefCallback}
                  src={generatePreviewUrl(contentData)}
                  alt="thumbnail"
                  width={48}
                  height={48}
                  className={`size-12 rounded-8 bg-no-repeat object-cover transition-opacity duration-300 ${
                    imageLoaded ? 'opacity-100' : 'absolute opacity-0'
                  }`}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  onDragStart={(e) => e.preventDefault()}
                  draggable={false}
                />
              </div>
            ) : (
              <div className="size-12 rounded-8 bg-feedback-skeleton-background" />
            )}
          </div>
          {/* Content */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            {/* Post text */}
            <div className="mr-2 truncate text-14 font-400 text-neutral-500">
              {content.relevant_snippet}
            </div>
            {/* Meta row */}
            <div className="mt-1 flex items-center text-neutral-500">
              <span className="mr-1 text-12 font-400">Latest post</span>
              <span className="mr-1 text-12 font-400">
                {contentData?.updatedAt
                  ? formatDate(
                      contentData.updatedAt,
                      MONTH_NAME_DAY_YEAR_FORMAT,
                    )
                  : ''}
              </span>
              <span className="flex items-center gap-1 text-12 font-400">
                {contentData?.platform && (
                  <IconBox
                    icon={
                      <ContentCardPlatformIcon
                        boxSize={4}
                        platform={
                          contentData.platform as ALL_SOCIAL_PLATFORM_TYPE
                        }
                      />
                    }
                    width={6}
                    height={6}
                  />
                )}
                <span>
                  {formatPlatformName(
                    contentData?.platform as ALL_SOCIAL_PLATFORM_TYPE,
                  )}
                </span>
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        onClick={handleContentClick}
        className={`flex h-12 min-w-12 shrink-0 cursor-pointer items-center justify-center rounded-8 transition-all ${
          isSelected
            ? 'border-2 border-orange-500 opacity-100'
            : 'relative border-2 border-neutral-0 opacity-100'
        }`}
      >
        {/* Dark overlay for non-selected items */}
        {!isSelected && (
          <div className="absolute inset-0 z-10 rounded-8 bg-neutral-900 opacity-40" />
        )}

        {imageError || (contentData && !imageUrl) ? (
          <div className="flex size-full items-center justify-center rounded-8 bg-neutral-100">
            <div className="text-12 text-neutral-300">N/A</div>
          </div>
        ) : contentData ? (
          <div className="relative size-full">
            {/* Skeleton loader */}
            {!imageLoaded && (
              <div className="size-full rounded-8 bg-feedback-skeleton-background" />
            )}
            {/* Actual image */}
            <img
              ref={imageRefCallback}
              src={generatePreviewUrl(contentData)}
              alt="thumbnail"
              width={48}
              height={48}
              className={`size-full rounded-8 object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'absolute opacity-0'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              onDragStart={(e) => e.preventDefault()}
              draggable={false}
            />
          </div>
        ) : (
          <div className="size-full rounded-8 bg-feedback-skeleton-background" />
        )}
      </div>
    );
  },
);

ContentItem.displayName = 'ContentItem';
